import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import packageJson from '../../package.json';
import axios from 'axios';

const Header = () => {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [apiVersion, setApiVersion] = useState(null);

    useEffect(() => {
        const fetchApiVersion = async () => {
            try {
                const response = await axios.get('/api/health');
                if (response.data?.versions?.server) {
                    console.log('API Version:', response.data.versions.server);
                    setApiVersion(response.data.versions.server);
                }
            } catch (error) {
                console.error('Failed to fetch API version:', error);
            }
        };

        console.log('Fetching API version...');
        fetchApiVersion();
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <header className="bg-white shadow-md sticky top-0 z-50">
            <nav className="container mx-auto px-4 py-4" aria-label="Main navigation">
                <div className="flex justify-between items-center">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-xl">S</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-2xl font-bold text-gray-800 leading-tight">SOKBA</span>
                            <span className="text-[10px] text-gray-400 leading-none">
                                v{packageJson.version}
                                {apiVersion && ` • API v${apiVersion}`}
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-6">
                        <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors">
                            {t('nav.home')}
                        </Link>
                        <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors">
                            {t('nav.about')}
                        </Link>
                        <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors">
                            {t('nav.contact')}
                        </Link>

                        {user ? (
                            <>
                                <Link
                                    to="/dashboard"
                                    className="text-gray-700 hover:text-blue-600 transition-colors"
                                >
                                    {t('nav.dashboard')}
                                </Link>
                                <Link
                                    to="/patients"
                                    className="text-gray-700 hover:text-blue-600 transition-colors"
                                >
                                    {t('nav.patients')}
                                </Link>
                                <Link
                                    to="/organization-settings"
                                    className="text-gray-700 hover:text-blue-600 transition-colors"
                                >
                                    {t('nav.organization')}
                                </Link>
                                <LanguageSwitcher />
                                <div className="flex items-center space-x-4">
                                    <Link
                                        to="/profile"
                                        className="text-sm text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-2"
                                    >
                                        {user.profilePicture && (
                                            <img
                                                src={user.profilePicture}
                                                alt="Profile"
                                                className="w-8 h-8 rounded-full"
                                            />
                                        )}
                                        <span>
                                            {user.firstName} {user.organization && `• ${user.organization.name}`}
                                        </span>
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        {t('nav.logout')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <LanguageSwitcher />
                                <Link
                                    to="/login"
                                    className="text-gray-700 hover:text-blue-600 transition-colors"
                                >
                                    {t('nav.login')}
                                </Link>
                                <Link
                                    to="/register"
                                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {t('nav.register')}
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            {mobileMenuOpen ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            )}
                        </svg>
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden mt-4 pb-4 space-y-3">
                        <Link
                            to="/"
                            className="block text-gray-700 hover:text-blue-600 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            {t('nav.home')}
                        </Link>
                        <Link
                            to="/about"
                            className="block text-gray-700 hover:text-blue-600 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            {t('nav.about')}
                        </Link>
                        <Link
                            to="/contact"
                            className="block text-gray-700 hover:text-blue-600 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            {t('nav.contact')}
                        </Link>

                        {user ? (
                            <>
                                <Link
                                    to="/dashboard"
                                    className="block text-gray-700 hover:text-blue-600 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.dashboard')}
                                </Link>
                                <Link
                                    to="/patients"
                                    className="block text-gray-700 hover:text-blue-600 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.patients')}
                                </Link>
                                <Link
                                    to="/organization-settings"
                                    className="block text-gray-700 hover:text-blue-600 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.organization')}
                                </Link>
                                <Link
                                    to="/profile"
                                    className="block text-gray-700 hover:text-blue-600 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.profile')}
                                </Link>
                                <div className="pt-2 border-t">
                                    <div className="mb-3">
                                        <LanguageSwitcher />
                                    </div>
                                    <p className="text-sm text-gray-600 mb-2">
                                        {user.firstName} {user.organization && `• ${user.organization.name}`}
                                    </p>
                                    <button
                                        onClick={() => {
                                            handleLogout();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        {t('nav.logout')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="pt-2 border-t">
                                    <LanguageSwitcher />
                                </div>
                                <Link
                                    to="/login"
                                    className="block text-gray-700 hover:text-blue-600 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.login')}
                                </Link>
                                <Link
                                    to="/register"
                                    className="block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors text-center"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.register')}
                                </Link>
                            </>
                        )}
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
